/**
 * Map voice failures to fixed, user-safe messages.
 * Raw backend details, stack traces and gateway payloads are never surfaced.
 */

export const VOICE_ERROR = {
  SESSION_EXPIRED: 'session_expired',
  FORBIDDEN: 'forbidden',
  NOT_FOUND: 'not_found',
  SERVER: 'server',
  UNAVAILABLE: 'unavailable',
  TIMEOUT: 'timeout',
  WEBRTC: 'webrtc',
  MIC_DENIED: 'mic_denied',
  MIC_DISMISSED: 'mic_dismissed',
  MIC_UNAVAILABLE: 'mic_unavailable',
  MIC_IN_USE: 'mic_in_use',
  UNSUPPORTED: 'unsupported',
  AUDIO: 'audio',
  VOICE_SESSION_EXPIRED: 'voice_session_expired',
  MUTE_FAILED: 'mute_failed',
  STT_UNSUPPORTED: 'stt_unsupported',
};

export const VOICE_ERROR_MESSAGES = {
  [VOICE_ERROR.SESSION_EXPIRED]: 'Your sign-in has expired. Please sign in again to use voice.',
  [VOICE_ERROR.FORBIDDEN]: "You don't have access to voice for this project.",
  [VOICE_ERROR.NOT_FOUND]: 'This project is not available for voice.',
  [VOICE_ERROR.SERVER]: 'Voice could not start because of a server error. Please try again.',
  [VOICE_ERROR.UNAVAILABLE]: 'Voice is currently unavailable. Please try again later.',
  [VOICE_ERROR.TIMEOUT]: 'The voice service took too long to respond. Please try again.',
  [VOICE_ERROR.WEBRTC]: 'The voice connection could not be established. Check your network and try again.',
  [VOICE_ERROR.MIC_DENIED]: 'Microphone access was blocked. Allow microphone access in your browser to use voice.',
  [VOICE_ERROR.MIC_DISMISSED]: 'Microphone permission was dismissed. Start voice again and allow microphone access.',
  [VOICE_ERROR.MIC_UNAVAILABLE]: 'No microphone was found. Connect a microphone and try again.',
  [VOICE_ERROR.MIC_IN_USE]: 'Your microphone is in use by another application.',
  [VOICE_ERROR.UNSUPPORTED]: 'Voice is not supported in this browser.',
  [VOICE_ERROR.AUDIO]: 'Assistant audio could not be played.',
  [VOICE_ERROR.VOICE_SESSION_EXPIRED]: 'The voice session has ended or expired. Start voice again to continue.',
  [VOICE_ERROR.MUTE_FAILED]: 'The microphone setting could not be changed. Please try again.',
  [VOICE_ERROR.STT_UNSUPPORTED]: 'That speech engine is not available. Choose another and try again.',
};

export class VoiceError extends Error {
  constructor(code, cause) {
    super(VOICE_ERROR_MESSAGES[code] || VOICE_ERROR_MESSAGES[VOICE_ERROR.SERVER]);
    this.name = 'VoiceError';
    this.code = code;
    if (cause) this.cause = cause;
  }
}

export const isAbortError = (err) =>
  err?.name === 'AbortError' || err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED';

export const micErrorCode = (err) => {
  const name = err?.name || '';
  if (name === 'NotAllowedError' || name === 'PermissionDeniedError' || name === 'SecurityError') {
    return /dismiss/i.test(String(err?.message || '')) ? VOICE_ERROR.MIC_DISMISSED : VOICE_ERROR.MIC_DENIED;
  }
  if (name === 'NotFoundError' || name === 'DevicesNotFoundError' || name === 'OverconstrainedError') {
    return VOICE_ERROR.MIC_UNAVAILABLE;
  }
  if (name === 'NotReadableError' || name === 'TrackStartError' || name === 'AbortError') {
    return VOICE_ERROR.MIC_IN_USE;
  }
  return VOICE_ERROR.MIC_UNAVAILABLE;
};

/**
 * @param {unknown} err  axios error, VoiceError, or anything else
 * @param {'create'|'signal'} stage  create = POST /voice/session; signal = offer/status for an existing session
 */
export const toVoiceErrorCode = (err, stage = 'create') => {
  if (err instanceof VoiceError) return err.code;
  const status = err?.response?.status;
  if (status === 401) return VOICE_ERROR.SESSION_EXPIRED;
  if (status === 403) return VOICE_ERROR.FORBIDDEN;
  if (status === 404) return stage === 'signal' ? VOICE_ERROR.VOICE_SESSION_EXPIRED : VOICE_ERROR.NOT_FOUND;
  const detail = String(err?.response?.data?.detail || '');
  if (status === 400 && /speech engine/i.test(detail)) return VOICE_ERROR.STT_UNSUPPORTED;
  if (status === 504 || err?.code === 'ECONNABORTED' || err?.code === 'ETIMEDOUT') return VOICE_ERROR.TIMEOUT;
  if (status === 503) return VOICE_ERROR.UNAVAILABLE;
  if (typeof status === 'number' && status >= 500) return VOICE_ERROR.SERVER;
  if (typeof status === 'number' && status >= 400) return VOICE_ERROR.SERVER;
  if (err?.code === 'ERR_NETWORK' || (err?.request && !err?.response)) return VOICE_ERROR.UNAVAILABLE;
  return VOICE_ERROR.SERVER;
};

export const voiceErrorMessage = (code) =>
  VOICE_ERROR_MESSAGES[code] || VOICE_ERROR_MESSAGES[VOICE_ERROR.SERVER];
